import { useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  faArrowDown,
  faArrowUp,
  faCalculator,
  faQuestionCircle,
} from '@fortawesome/pro-regular-svg-icons'
import { faChartSimple } from '@fortawesome/pro-solid-svg-icons'
import { Card, CardColors, Icon, Link, Popup, Select, Text, Image } from '@/ui'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import {
  selectMonthToDateIncomeChartData,
  selectPercDiffLastMonth,
} from '@/features/user/dashboard/insights-cards/monthToDateIncome.selectors'
import { centsToDollars, formatCurrency } from '@/utils/currencyHelpers'
import { TextColors, TypographyStyles } from '@/styles'
import { MonthToDateIncomeCardCustomTooltip } from '@/features/user/dashboard/insights-cards/MonthToDateIncomeCardCustomTooltip'
import { fetchProfitsBreakdown } from '@/actions/reportActions'

// Shortens numbers into using k and M
const formatter = Intl.NumberFormat('en', { notation: 'compact' })

const legendFormatter = (value: string) => (
  <Text
    variant={TypographyStyles.BODY_XS}
    color={TextColors.MUTED}
    style={{ display: 'inline-block' }}
  >
    {value}
  </Text>
)

const PAST_VALUE_OPTIONS: { value: 'month' | 'year'; text: string }[] = [
  { value: 'month', text: 'This month vs last month' },
  { value: 'year', text: 'This month vs last year' },
]

export const MonthToDateIncomeCard = () => {
  const [pastValue, setPastValue] = useState<'month' | 'year'>('month')

  const todayDate = useMemo(() => DateTime.now(), [])
  const pastDate = useMemo(
    () =>
      pastValue === 'month'
        ? todayDate.minus({ month: 1 })
        : todayDate.minus({ year: 1 }),
    [pastValue, todayDate]
  )

  const currentMonthIncomeResponse = useFetchResponse(fetchProfitsBreakdown, {
    startDate: todayDate.startOf('month'),
    endDate: todayDate.endOf('month'),
  })

  const pastMonthIncomeResponse = useFetchResponse(fetchProfitsBreakdown, {
    startDate: pastDate.startOf('month'),
    endDate: pastDate.endOf('month'),
  })

  const percDiffLastMonth = useReselector(
    selectPercDiffLastMonth,
    todayDate,
    pastDate
  )
  const chartData = useReselector(selectMonthToDateIncomeChartData, pastValue)

  const percentageToDisplay =
    percDiffLastMonth && Math.abs(percDiffLastMonth * 100).toFixed(0)

  return (
    <Card>
      <div className="flex flex-col sm:flex-row sm:items-start mb-2">
        <div className="sm:w-1/2">
          <Text variant={TypographyStyles.H1} className="header-text">
            Month to Date Income
          </Text>
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            {todayDate
              .startOf('month')
              .toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}{' '}
            - {todayDate.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}
          </Text>
        </div>
        <div className="mt-2 sm:mt-0 sm:w-1/2 flex sm:justify-end">
          <span className="hidden sm:inline-flex mr-6">
            <Popup
              trigger={
                <Link
                  href="/income-planning"
                  iconLeft={faCalculator}
                  color={TextColors.HEADER}
                  className="body-sm whitespace-nowrap"
                >
                  <span className="hidden lg:inline">Income Planning</span>
                </Link>
              }
              content={
                <Text variant={TypographyStyles.BODY_SM}>Income Planning</Text>
              }
            />
          </span>
          <div className="w-full sm:w-60">
            <Select
              options={PAST_VALUE_OPTIONS}
              onValueChange={setPastValue}
              value={pastValue}
            />
          </div>
        </div>
      </div>
      <div className="grid auto-rows-fr md:grid-cols-2 md:gap-x-4">
        <div className="flex">
          <Card color={CardColors.LEVEL_1} className="w-full my-2 md:my-4">
            <Text variant={TypographyStyles.LABEL_MD} className="mb-4">
              This Month
            </Text>
            <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
              <div className="min-w-0 max-w-full">
                <Text
                  variant={TypographyStyles.DISPLAY_MD}
                  color={TextColors.HEADER}
                  className="mb-1 break-words"
                >
                  {currentMonthIncomeResponse
                    ? formatCurrency(currentMonthIncomeResponse.sum)
                    : '$-'}
                </Text>
                <Text
                  variant={TypographyStyles.LABEL_SM}
                  color={TextColors.MUTED}
                  className="truncate"
                >
                  {todayDate.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)} income to
                  date
                  <Popup
                    tooltipTriggerClassName="align-middle"
                    trigger={
                      <Icon
                        icon={faQuestionCircle}
                        className="ml-1"
                        color={TextColors.MUTED}
                        style={{ fontSize: 16 }}
                      />
                    }
                    content={
                      <>
                        <Text variant={TypographyStyles.H3}>
                          Income to Date
                        </Text>
                        <br />
                        <Text variant={TypographyStyles.BODY_SM}>
                          This is the total income that has been categorized so
                          far this month.
                          <br />
                          <br />
                          The percentage change shows how your current monthly
                          income compares to the same point in your selected
                          comparison period (Last Month, or This Time Last
                          Year).
                        </Text>
                      </>
                    }
                  />
                </Text>
              </div>
              <div className="shrink-0">
                {/* display-md line box keeps the smaller percentage on the amount's baseline */}
                <div className="display-md mb-1 whitespace-nowrap">
                  {percentageToDisplay ? (
                    <>
                      <Icon
                        icon={percDiffLastMonth < 0 ? faArrowDown : faArrowUp}
                        color={
                          percDiffLastMonth > 0
                            ? 'text-green-100'
                            : 'text-orange-200'
                        }
                        size="lg"
                        backgroundClassName="inline-flex w-auto h-auto mr-1 text-base leading-none"
                      />
                      <Text
                        variant={TypographyStyles.DISPLAY_XS}
                        color={
                          percDiffLastMonth > 0
                            ? TextColors.HIGHLIGHT
                            : TextColors.RED
                        }
                        className="inline"
                      >
                        {percentageToDisplay}%
                      </Text>
                    </>
                  ) : (
                    <Text
                      variant={TypographyStyles.DISPLAY_XS}
                      className="inline"
                    >
                      -
                    </Text>
                  )}
                </div>
                <Text
                  variant={TypographyStyles.LABEL_SM}
                  color={TextColors.MUTED}
                  className="truncate"
                >
                  vs this time in{' '}
                  {pastDate.toFormat(
                    pastValue === 'month'
                      ? 'LLL'
                      : DATE_FORMATS_LUXON.MONTH_YEAR
                  )}
                </Text>
              </div>
            </div>
          </Card>
        </div>
        <div className="flex">
          <Card color={CardColors.LEVEL_1} className="w-full my-2 md:my-4">
            <Text variant={TypographyStyles.LABEL_MD} className="mb-4">
              {pastValue === 'month' ? 'Last Month' : 'This Time Last Year'}
            </Text>
            <div>
              {pastMonthIncomeResponse ? (
                <>
                  <div className="flex items-end mb-1">
                    <Text
                      variant={TypographyStyles.DISPLAY_MD}
                      color={TextColors.HEADER}
                      className="min-w-0 break-words"
                    >
                      {formatCurrency(pastMonthIncomeResponse.sum)}
                    </Text>
                  </div>
                  <Text
                    variant={TypographyStyles.LABEL_SM}
                    color={TextColors.MUTED}
                    className="truncate"
                  >
                    {pastDate.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)} total
                    income
                    {pastValue === 'month' ? (
                      <Popup
                        tooltipTriggerClassName="align-middle"
                        trigger={
                          <Icon
                            icon={faQuestionCircle}
                            className="ml-1"
                            color={TextColors.MUTED}
                            style={{ fontSize: 16 }}
                          />
                        }
                        content={
                          <>
                            <Text variant={TypographyStyles.H3}>
                              Last Month Total Income
                            </Text>
                            <br />
                            <Text variant={TypographyStyles.BODY_SM}>
                              This is the total income you earned the previous
                              month.
                            </Text>
                          </>
                        }
                      />
                    ) : (
                      <Popup
                        tooltipTriggerClassName="align-middle"
                        trigger={
                          <Icon
                            icon={faQuestionCircle}
                            className="ml-1"
                            color={TextColors.MUTED}
                            style={{ fontSize: 16 }}
                          />
                        }
                        content={
                          <>
                            <Text variant={TypographyStyles.H3}>
                              This Time Last Year
                            </Text>
                            <br />
                            <Text variant={TypographyStyles.BODY_SM}>
                              This is the total income you earned the same month
                              the previous year.
                            </Text>
                          </>
                        }
                      />
                    )}
                  </Text>
                </>
              ) : (
                <Card className="bg-moss-0">
                  <div className="flex">
                    <div className="w-3/16 flex justify-center items-center">
                      <Icon icon={faChartSimple} />
                    </div>
                    <div className="w-13/16">
                      <Text variant={TypographyStyles.BODY_SM}>
                        We don’t have historical data yet, but check back soon
                        for updates.
                      </Text>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </Card>
        </div>
      </div>

      <div className="relative mt-6">
        <ResponsiveContainer width="99%" height={250}>
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                <stop stopColor="var(--color-green-50)" />
                <stop
                  offset={1}
                  stopColor="var(--color-green-50)"
                  stopOpacity={0}
                  startOffset={1}
                />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="dayOfMonth"
              tickFormatter={(value) =>
                pastValue === 'month'
                  ? `Day ${value}`
                  : `${todayDate.toFormat(DATE_FORMATS_LUXON.MONTH)} ${value}`
              }
              tick={{
                fontSize: 12,
                fontFamily: 'var(--font-secondary)',
                fill: 'var(--color-neutral-500)',
              }}
              minTickGap={60}
              axisLine={{ stroke: 'white' }}
              tickLine={false}
              tickMargin={16}
            />
            <YAxis
              domain={[0, 'auto']}
              type="number"
              allowDecimals={false}
              tickFormatter={(value) =>
                `$${formatter.format(centsToDollars(value))}`
              }
              tick={{
                fontSize: 12,
                fontFamily: 'var(--font-secondary)',
                fill: 'var(--color-neutral-500)',
              }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip<number, string>
              content={(props) => (
                <MonthToDateIncomeCardCustomTooltip
                  {...props}
                  pastValue={pastValue}
                />
              )}
            />
            <Legend
              iconType="square"
              align="center"
              formatter={legendFormatter}
              wrapperStyle={{ paddingTop: 16 }}
              payload={[
                {
                  value: `${todayDate.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)} Income`,
                  type: 'square',
                  color: 'var(--color-green-50)',
                },
                {
                  value: `${pastDate.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)} Total Income`,
                  type: 'plainline',
                  color: 'var(--color-neutral-900)',
                  payload: {
                    strokeDasharray: 0,
                  },
                },
              ]}
            />
            <Area
              type="linear"
              dataKey="current"
              name="Current"
              stroke="var(--color-green-50)"
              strokeWidth={2}
              fill="url(#colorUv)"
            />
            <Area
              type="linear"
              dataKey="past"
              name="Past"
              strokeWidth={2}
              stroke="var(--color-neutral-900)"
              fillOpacity={0}
            />
          </AreaChart>
        </ResponsiveContainer>
        {!currentMonthIncomeResponse && !pastMonthIncomeResponse && (
          <div className="bg-neutral-25 absolute top-0 right-[40px] bottom-[50px] left-[60px] flex items-center justify-center flex-col">
            <Image
              src="https://heard-images.s3.amazonaws.com/assets/metrics.svg"
              alt="metrics"
              className="mb-1 w-[80px]"
            />
            <Text variant={TypographyStyles.BODY_SM}>
              This chart will update once your income has been categorized.
            </Text>
          </div>
        )}
      </div>
    </Card>
  )
}
